import React from 'react';

import { useTranslations } from 'next-intl';

import { ProductDescription } from './ProductDescription';
import styles from './styles.module.css';

import { PurchaseBreakdownRows } from '@/app/[locale]/pay/components/PurchaseBreakdownRows';
import { SoldOutBanner } from '@/app/[locale]/pay/components/SoldOutBanner';
import { InlineCampaignBanners } from '@/components/InlineCampaignBanners';
import {
  StockStatus,
  type CheckoutProduct,
  type OrderSummaryData,
} from '@/modules/checkout/types';

type Props = {
  checkoutSummary: OrderSummaryData;
  products: CheckoutProduct[];
  canShipItem: boolean;
  isInternationalShippingUnavailable: boolean;
  withSoldOutHandling?: boolean;
  withRemoveButton?: boolean;
};

export function PurchaseBreakdown({
  checkoutSummary,
  products,
  canShipItem,
  isInternationalShippingUnavailable,
  withSoldOutHandling = false,
  withRemoveButton = false,
}: Props) {
  const t = useTranslations('checkout');

  const soldOutProducts = products.filter((product) =>
    checkoutSummary.lines.some(
      (line) =>
        line.product_id === product.id &&
        line.stock_status === StockStatus.NotAvailable
    )
  );

  const hasSoldOutProducts = soldOutProducts.length > 0;
  const allProductsSoldOut =
    hasSoldOutProducts && soldOutProducts.length === products.length;
  const shippingNotIncluded =
    checkoutSummary.total_price_components?.shipping_price === false;

  return (
    <>
      {/* eslint-disable-next-line no-restricted-syntax */}
      <div className={styles.products} data-testid="orderSummary__products">
        {products.map((product) => (
          <ProductDescription
            key={product.id}
            product={product}
            checkoutSummary={checkoutSummary}
            isInternationalShippingUnavailable={
              isInternationalShippingUnavailable
            }
            withSoldOutHandling={withSoldOutHandling}
            withRemoveButton={withRemoveButton}
          />
        ))}
      </div>

      {withSoldOutHandling && hasSoldOutProducts && (
        <SoldOutBanner
          title={t('GuestSummary.SoldOut.Title', {
            description: soldOutProducts[0].description,
            otherCount: soldOutProducts.length - 1,
          })}
          description={t('GuestSummary.SoldOut.Description', {
            otherCount: soldOutProducts.length - 1,
          })}
        />
      )}

      {!isInternationalShippingUnavailable && (
        <>
          <PurchaseBreakdownRows
            checkoutSummary={checkoutSummary}
            products={products}
            canShipItem={canShipItem}
            isInternationalShippingUnavailable={
              isInternationalShippingUnavailable
            }
            totalLabel={t('OrderSummaryTotal')}
            hideShippingRow={
              withSoldOutHandling && (allProductsSoldOut || shippingNotIncluded)
            }
          />
          <InlineCampaignBanners
            banners={checkoutSummary.banners}
            className={styles.campaignBanners}
          />
        </>
      )}
    </>
  );
}
